// frontend/src/pages/Adventure.tsx
import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import scrollImg from '../assets/adventure/scroll.png';
import buttonImg from '../assets/adventure/button.png';
import fightImg from '../assets/adventure/fight1.png';
import './Adventure.css';

type Event = {
  id: string;
  type: string;
  text: string;
  xpGained?: number;
  hpLost?: number;
  silverGained?: number;
  goldGained?: number;
  choices?: string[];
};

export default function Adventure() {
  const [cooldown, setCooldown] = useState<number>(0);
  const [event, setEvent]       = useState<Event | null>(null);
  const [loading, setLoading]   = useState(false);

  // Tick down cooldown every second
  useEffect(() => {
    if (cooldown > 0) {
      const tid = setInterval(() => setCooldown(c => Math.max(0, c - 1)), 1000);
      return () => clearInterval(tid);
    }
  }, [cooldown]);

  // Kick off a new adventure
  async function goAdventure() {
    setLoading(true);
    try {
      const res = await api.post<{
        cooldownRemaining: number;
        event: Event | null;
      }>('/adventure');
      setCooldown(res.data.cooldownRemaining);
      setEvent(res.data.event);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  // Respond to an interactive event
  async function respond(choice: string) {
    if (!event) return;
    setLoading(true);
    try {
      const res = await api.post<{ event: Event }>('/adventure/respond', {
        eventId: event.id,
        choice
      });
      setEvent(res.data.event);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  // Reset to banner view
  function goBack() {
    setEvent(null);
  }

  return (
    <div className="adventure-page">
      <div className="adventure-container">
        {/* Banner with scroll, title, and button overlay */}
        <div className={`adventure-header${event || loading ? ' hidden' : ''}`}>
          <img src={scrollImg} alt="Scroll background" className="scroll-img" />
          <h2>Adventure</h2>
          {cooldown === 0 ? (
            <button
              className="adventure-button overlay"
              onClick={goAdventure}
              disabled={loading}
              style={{ backgroundImage: `url(${buttonImg})` }}
            >
              {loading ? '…going…' : 'Go on Adventure'}
            </button>
          ) : (
            <p className="cooldown-text overlay">Next adventure in {cooldown}s</p>
          )}
        </div>

        {/* Show fight image when adventure starts */}
        {(event || loading) && (
          <div className="fight-container">
            <img src={fightImg} alt="Fight scene" className="fight-img" />
          </div>
        )}

        {/* Show the latest event details when the scroll is hidden */}
        {event && (
          <div className="event-details">
            <p>{event.text}</p>
            {event.xpGained != null     && <p>+{event.xpGained} XP</p>}
            {event.hpLost != null       && <p>–{event.hpLost} HP</p>}
            {event.silverGained != null && <p>+{event.silverGained} silver</p>}
            {event.goldGained != null   && <p>+{event.goldGained} gold</p>}

            {event.choices && event.choices.length > 0 ? (
              <div className="choices">
                {event.choices.map(choice => (
                  <button
                    key={choice}
                    className="adventure-button choice-button"
                    onClick={() => respond(choice)}
                    disabled={loading}
                    style={{ backgroundImage: `url(${buttonImg})` }}
                  >
                    {choice.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </button>
                ))}
              </div>
            ) : (
              <button
                className="adventure-button overlay go-back-button"
                onClick={goBack}
                style={{ backgroundImage: `url(${buttonImg})` }}
              >
                Go Back
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
